import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { Box, Collapse, Stack } from "@mui/material";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { type ReactNode, useState } from "react";

import { Button, type ButtonProps } from "../../components/Button";
import { TextButton } from "../../components/TextButton";
import { useIsJobsEnabled } from "../../PlacementCandidate/useIsJobsEnabled";

interface InfoProps {
  clipboardScore: number;
}

interface ShowMoreProps {
  expanded: boolean;
  onClick: ButtonProps["onClick"];
}

function ShowMore(props: ShowMoreProps) {
  const { expanded, onClick } = props;

  const label = expanded ? "Show less" : "Learn More";

  return (
    <Button variant="outlined" size="small" onClick={onClick}>
      <Stack direction="row" spacing={4} justifyContent="center" alignItems="center">
        <Text>{label}</Text>
        <CbhIcon
          type="chevron-down"
          sx={(theme) => ({
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.short,
            }),
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          })}
        />
      </Stack>
    </Button>
  );
}

function InfoSection(props: { heading: string; description: string | ReactNode }) {
  return (
    <Stack spacing={2}>
      <Text variant="h6" sx={{ fontWeight: 500 }}>
        {props.heading}
      </Text>
      <Text
        variant="body2"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
        })}
      >
        {props.description}
      </Text>
    </Stack>
  );
}

export function Info(props: InfoProps) {
  const { clipboardScore } = props;

  const [expanded, setExpanded] = useState(false);
  const { openArticle } = useZendeskArticle();
  const { inviteRequiredSupportLink } = useSupportLinks();

  const isJobsEnabled = useIsJobsEnabled();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const content: Array<{ heading: string; description: string | ReactNode }> = [
    {
      heading: "What does this mean?",
      description: `You won't be able to book open shifts directly, but workplaces can still invite you to shifts directly. This status lasts indefinitely. ${
        isJobsEnabled ? "This status does not impact your access to Jobs on Clipboard" : ""
      }`,
    },
    {
      heading: "Why is my account invite-required?",
      description:
        "Due to safety, reliability, and/or professionalism concerns reported by multiple workplaces, your account was reviewed and placed in Invite-Required status.",
    },
    {
      heading: "What happened to my Clipboard Score?",
      description: `Your Clipboard Score is frozen at ${clipboardScore}, your account can neither gain nor lose points.`,
    },
    {
      heading: "Where can I learn more?",
      description: (
        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
          })}
        >
          You can visit our{" "}
          <TextButton
            onClick={async () => {
              await openArticle(inviteRequiredSupportLink);
            }}
          >
            knowledge base
          </TextButton>{" "}
          to learn more.
        </Text>
      ),
    },
  ];

  return (
    <Box>
      <Stack spacing={7} marginTop={6}>
        <InfoSection heading={content[0].heading} description={content[0].description} />
        <Collapse unmountOnExit timeout="auto" in={expanded}>
          <Stack spacing={7}>
            {content.slice(1).map(({ heading, description }) => (
              <InfoSection key={heading} heading={heading} description={description} />
            ))}
          </Stack>
        </Collapse>
      </Stack>
      <Stack marginY={6}>
        <ShowMore expanded={expanded} onClick={handleExpandClick} />
      </Stack>
    </Box>
  );
}
